import { Component, OnInit } from '@angular/core';
import { CreateAnnouncementView } from 'app/models/announcements/CreateAnnouncementView';
import { User } from 'app/models/User';
import { AnnouncementService } from 'app/services/Announcement/announcement.service';
import { LoadingService } from 'app/services/loading.service';
import { NotificationService } from 'app/services/notification.service';
import { UserService } from 'app/services/user.service';
import { UtilityService } from 'app/services/utility.service';
import { SanitizationService } from 'app/shared/utils/sanitization.service';

@Component({
	selector: 'app-announcements-view',
	templateUrl: './announcements-view.component.html',
	styleUrls: ['./announcements-view.component.css'],
	standalone: false,
})
export class AnnouncementsViewComponent implements OnInit {
	private clientId: string;
	private userId: string;
	allUsers: User[];
	data: CreateAnnouncementView[] = [];
	pageNumber: number = 1;
	pageSize: number = 10;
	hasMore: boolean = true;
	isLoadingMore: boolean = false;
	expandedAnnouncements: Set<string> = new Set<string>();
	loadingFullMessage: Set<string> = new Set<string>();
	consecutiveLoadMoreFailures: number = 0;
	readonly MAX_LOAD_MORE_RETRIES: number = 3;

	constructor(
		private loadingService: LoadingService,
		private notifyService: NotificationService,
		private announcementService: AnnouncementService,
		public utilityService: UtilityService,
		private userService: UserService,
		private readonly sanitizationService: SanitizationService
	) {
		this.clientId = this.userService.currentUserValue.user.clientId;
		this.userId = this.userService.currentUserValue.user.id;
	}

	ngOnInit(): void {
		this.loadingService.OnLoading.emit(true);
		const allUsersRequest = this.userService.getUsers().toPromise();
		const announcementRequest = this.announcementService
			.getAnnouncementByUserId(this.clientId, this.userId, this.pageNumber, this.pageSize + 1)
			.toPromise();

		Promise.all([allUsersRequest, announcementRequest]).then(
			([allUsersResponse, announcementResponse]) => {
				this.allUsers = allUsersResponse;
				this.hasMore = announcementResponse.length > this.pageSize;
				this.data = announcementResponse.slice(0, this.pageSize);
				this.loadingService.OnLoading.emit(false);
			},
			(error) => {
				this.loadingService.OnLoading.emit(false);
				this.notifyService.showError('Unable to load list.', 'Error');
			}
		);
	}

	loadMore(): void {
		if (!this.hasMore || this.isLoadingMore) {
			return;
		}

		if (this.consecutiveLoadMoreFailures >= this.MAX_LOAD_MORE_RETRIES) {
			this.notifyService.showError(
				'Unable to load more announcements after multiple attempts. Please refresh the page or contact support if the problem persists.',
				'Error'
			);
			this.hasMore = false;
			return;
		}

		this.isLoadingMore = true;
		this.pageNumber++;

		this.announcementService
			.getAnnouncementByUserId(this.clientId, this.userId, this.pageNumber, this.pageSize + 1)
			.subscribe(
				(response) => {
					if (response && response.length > 0) {
						this.hasMore = response.length > this.pageSize;
						const itemsToAdd = response.slice(0, this.pageSize);
						this.data = [...this.data, ...itemsToAdd];
					} else {
						this.hasMore = false;
					}
					this.consecutiveLoadMoreFailures = 0;
					this.isLoadingMore = false;
				},
				(error) => {
					this.consecutiveLoadMoreFailures++;
					const remainingRetries = this.MAX_LOAD_MORE_RETRIES - this.consecutiveLoadMoreFailures;

					if (remainingRetries > 0) {
						this.notifyService.showError(
							`Failed to load additional announcements. ${remainingRetries} attempt(s) remaining.`,
							'Error'
						);
					} else {
						this.notifyService.showError(
							'Unable to load more announcements after multiple attempts. Please refresh the page or contact support if the problem persists.',
							'Error'
						);
						this.hasMore = false;
					}

					this.isLoadingMore = false;
					this.pageNumber--;
				}
			);
	}

	markAsRead(announcementForId, index) {
		this.loadingService.OnLoading.emit(true);
		this.callMarkAsRead(announcementForId, index);
	}

	markAllAsRead() {
		this.data.forEach((announcement, index) => {
			this.loadingService.OnLoading.emit(true);
			this.callMarkAsRead(announcement.announcementFors[0].id, index);
		});
	}

	callMarkAsRead(announcementForId: string, index: number) {
		this.announcementService.announcementMarkAsRead(this.clientId, announcementForId, this.userId).subscribe(
			(response) => {
				this.loadingService.OnLoading.emit(false);
				this.data[index].announcementFors[0].isRead = true;
				localStorage.setItem('unreadAnnouncements', this.data.filter((x) => !x.announcementFors[0].isRead).length + '');
			},
			(error) => {
				this.loadingService.OnLoading.emit(false);
				this.notifyService.showError('Unable mark as read', 'Error');
			}
		);
	}

	markAsArchive(announcementForId, index) {
		this.loadingService.OnLoading.emit(true);
		this.callMarkAsArchive(announcementForId, index);
	}

	markAllAsArchive() {
		this.data.forEach((announcement, index) => {
			this.loadingService.OnLoading.emit(true);
			this.callMarkAsArchive(announcement.announcementFors[0].id, index);
		});
	}

	callMarkAsArchive(announcementForId: string, index: number) {
		this.announcementService.announcementMarkAsArchive(this.clientId, announcementForId, this.userId).subscribe(
			(response) => {
				this.loadingService.OnLoading.emit(false);
				this.data.splice(index, 1);
				localStorage.setItem('unreadAnnouncements', this.data.filter((x) => !x.announcementFors[0].isRead).length + '');
			},
			(error) => {
				this.loadingService.OnLoading.emit(false);
				this.notifyService.showError('Unable mark as read', 'Error');
			}
		);
	}

	getUserEmail(userId) {
		let user = this.allUsers.find((x) => x.id == userId);
		return user?.email;
	}

	toggleFullMessage(announcementId: string, index: number): void {
		// If already expanded, collapse it
		if (this.expandedAnnouncements.has(announcementId)) {
			this.expandedAnnouncements.delete(announcementId);
			return;
		}

		// If already loading, ignore
		if (this.loadingFullMessage.has(announcementId)) {
			return;
		}

		// Fetch full announcement
		this.loadingFullMessage.add(announcementId);
		this.announcementService.get(this.clientId, announcementId).subscribe(
			(response) => {
				// Replace shortMessage with full message in the data array
				this.data[index].message = response.message;
				this.expandedAnnouncements.add(announcementId);
				this.loadingFullMessage.delete(announcementId);
			},
			(error) => {
				this.notifyService.showError('Unable to load full message.', 'Error');
				this.loadingFullMessage.delete(announcementId);
			}
		);
	}

	isExpanded(announcementId: string): boolean {
		return this.expandedAnnouncements.has(announcementId);
	}

	isLoadingMessage(announcementId: string): boolean {
		return this.loadingFullMessage.has(announcementId);
	}

	isTruncated(announcement: CreateAnnouncementView): boolean {
		return announcement.shortMessage && announcement.shortMessage.endsWith('...');
	}

	hasHiddenContent(announcement: CreateAnnouncementView): boolean {
		if (this.isTruncated(announcement)) return true;
		if (!announcement.shortMessage) return false;
		return !announcement.message || announcement.shortMessage !== announcement.message;
	}

	getExpandLabel(announcement: CreateAnnouncementView): string {
		if (this.isExpanded(announcement.id)) return 'Show Less';
		return this.isTruncated(announcement) ? 'View Full Message' : 'Show Links';
	}

	getSanitizedHtml(announcement: CreateAnnouncementView): string {
		const html = this.isExpanded(announcement.id)
			? announcement.message
			: announcement.shortMessage || announcement.message;
		return this.sanitizationService.sanitizeHtml(html);
	}
}
